import type { EventNames, EventProperties, MixpanelManager } from '@/config';
import { captureError } from '@/config';

/** Creator participation funnel event names. */
export const CREATOR_FUNNEL_EVENTS = {
  INVITE_VIEWED: 'creator_invite_viewed',
  INVITE_ACCEPTED: 'creator_invite_accepted',
  INVITE_DECLINED: 'creator_invite_declined',
  PRODUCT_CLAIMED: 'creator_product_claimed',
  TASK_SUBMITTED: 'creator_task_submitted',
} as const satisfies Record<string, EventNames>;

/**
 * Tracks a funnel event only when all required source values are present.
 * Pass values before string coercion so missing values remain detectable.
 * Returns false only when Mixpanel throws, allowing callers to retry.
 */
export function trackFunnelEvent<E extends EventNames>(
  mixpanel: MixpanelManager,
  eventName: E,
  properties: EventProperties<E>,
  requiredValues: unknown[]
): boolean {
  const missingRequiredValue = requiredValues.some((value) => !value);

  if (missingRequiredValue) {
    captureError(new Error(`${eventName}: missing data`), { ...properties });
    return true;
  }

  try {
    mixpanel.event(eventName, properties);
    return true;
  } catch {
    // Analytics failures are non-critical and must never break the calling flow.
    return false;
  }
}
